/**
 * Product decisions made at development level, awaiting TRF's reaction.
 *
 * The rule this file exists to serve: **a decision nobody has ratified is not
 * a reason to stop building.** trfimagine.org is a production prototype whose
 * whole job is to let Ana, Azalea and Melanie *feel* a workflow and react to
 * it. Asking them to answer an open question in the abstract costs a meeting
 * and produces a worse answer than showing them a built screen and asking
 * "is this right?".
 *
 * So we choose, we build, and we mark the choice on the screen where it
 * shows up. They decide between options they can see.
 *
 * Every entry here is a real fork with a real alternative — not a disclaimer.
 * If there was never a second option worth naming, it is not a pending
 * decision, it is just a design, and it does not belong in this file.
 *
 * Retiring an entry: when TRF rules, delete it here (the marker disappears
 * from every surface at once) and append the ruling to `docs/DECISIONS.md`.
 * The marker is the pending state; DECISIONS is the settled state. Nothing
 * should ever be in both.
 */


export interface PendingDecisionSpec {
  /** The question in TRF's language, not the PRD's. No F-numbers, no OQ ids. */
  question: string;
  /** What is on screen right now. */
  chose: string;
  /** One sentence. Why this, over the others. */
  because: string;
  /** The options we did not take. Naming them is what makes this a decision. */
  alternatives: string[];
  /** Internal trace — issue / PRD row. Rendered only in the title attribute. */
  ref?: string;
}

export const PENDING_DECISIONS = {

  // #2230 — per-stage clocks replace the single 14-day "no staff action"
  // threshold. The targets are Victor's (2026-10-06); these five entries are
  // the defaults built around them. Numbers come from the constants.
  // #2714 — the Incomplete add-a-check picker.
  'tasks-status-shape': {
    question: 'Is task status a filter or a section?',
    chose: 'A filter: status narrows the list from the filter popover and shows as a word on the row (“In progress”); the list itself is grouped by when a task is due.',
    because:
      'Tasks are one record type. Health’s sections are different records; a status row above the list would be a second navigation for one list.',
    alternatives: ['Underline sections To do · In progress · Done above the list'],
    ref: 'Declutter spec 2026-09-10 §4 · Victor 2026-09-10 (“1-4 are fine”)',
  },

  'tasks-grouped-by-when': {
    question: 'How is the task list laid out?',
    chose: 'One line per task, grouped by when it is due — Overdue, Today, Tomorrow, Next seven days, Later, No date — with finished tasks in a Done group at the end that opens on request.',
    because:
      'That is the layout every task manager a parent already knows converges on (Todoist, Things, Asana); a card grid stopped scanning at a dozen tasks and said each fact in a box.',
    alternatives: [
      'Cards in a three-column grid grouped by status — the layout before 2026-09-10',
      'One flat list sorted by date with no group headings',
      'Grouped by status (To do · In progress · Done) as rows rather than cards',
    ],
    ref: 'Victor 2026-09-10 (“switch to a proven task manager format”) · DECISIONS 2026-09-10',
  },

  // #3558 — the "Came in through" line on the family profile.
  'came-in-pdf-over-sheet': {
    question: 'When a family was imported from both a printed PDF and the Google Form sheet, what does “Came in through” say?',
    chose: 'It says the printed response that Claude read. Any answer on the family may be Claude’s reading, so the line names the source a TRFer should check more carefully.',
    because:
      'The line is there so a TRFer knows how far to trust the answers. Saying “sheet” when some answers came from Claude reading a PDF would claim more certainty than the record has.',
    alternatives: [
      'Name both: “from the sheet and a printed response that Claude read”',
      'Name the sheet, because the sheet’s answers win where both gave one',
    ],
    ref: '#3558',
  },

  // Add an entry here in the SAME change that renders its marker, never
  // ahead of it. An entry whose `<PendingDecision>` is on no screen is a
  // decision we told ourselves we disclosed — which is the drift this
  // codebase keeps having to clean up, in a new costume.
  //
  // NOTE: the 14 in the copy above mirrors WAITING_ON_FAMILY_STALE_DAYS in
  // `backend/src/routes/trfer.js`. They are prose and logic, so they cannot
  // share a constant across the wire — if one moves, move the other. Both
  // sites carry this warning.

  'calendar-views-as-tabs': {
    question: 'Are Agenda / Week / Month sections or a view switch?',
    chose: 'A segmented control on the toolbar row; the calendar has one headline.',
    because:
      'A view that is not a URL is not a tab (design-grammar §4.5), and “Your week.” says less than the month label already does.',
    alternatives: ['Underline sections with a ?view= address, headline changing per view'],
    ref: 'Declutter spec 2026-09-10 §5 · Victor 2026-09-10',
  },

  'calendar-companion-column': {
    question: 'At which width does the calendar’s companion column open?',
    chose: 'Extra-large screens only; below that the card stacks above the grid.',
    because: 'The month grid needs width that a 300px column at 1024px does not leave it.',
    alternatives: ['Open at large screens, as Health does'],
    ref: 'Declutter spec 2026-09-10 §5 · Victor 2026-09-10',
  },

  'expenses-ledger-shape': {
    question: 'How does the Expenses & receipts list work?',
    chose:
      'One record per expense, receipt required before TRF sees it, grouped by month with a “Needs your attention” group first — the Bill.com Spend & Expense / Expensify shape.',
    because:
      'A collector has one object with an optional receipt and an incomplete state; two lists (receipts here, expenses there) made a parent file the same cost twice.',
    alternatives: [
      'Separate receipts and expenses lists',
      'Groups by status (needs attention · under review · paid) instead of by month',
      'A flat list, newest first',
    ],
    ref: 'Finances collector spec 2026-09-10 §4 · Victor 2026-09-10',
  },

  'finances-upcoming-planner': {
    question: 'Does the due-date planner (“Upcoming expenses”) stay on Finances?',
    chose: 'No — the page records what was spent; planning what is coming is a different tool and had no rows.',
    because:
      'Bill.com and Expensify have no planner; keeping one beside a ledger split “an expense” into two objects.',
    alternatives: ['Keep it as an “Upcoming” group at the top of the ledger'],
    ref: 'Finances collector spec 2026-09-10 §4.1 · Victor 2026-09-10',
  },

  'community-reactions': {
    question: 'Which reactions can a family leave on a post?',
    chose: 'Heart, hug, celebrate — each a drawn icon alone, no word beside it and no emoji.',
    because:
      'A heart reads as plain support in every study we found; a hug fits a hard day and a celebration fits TRF’s good news. “Sad” was left out on purpose — it divided readers in a health setting.',
    alternatives: [
      'A single heart, as CaringBridge ran for nine years',
      'Heart · Thinking of you · Me too, spelled out (the previous set)',
      'The same three as emoji (shipped 2026-09-10, removed 2026-09-12)',
    ],
    ref: 'Community rebuild 2026-09-10 · CSCW 2023 CaringBridge study · emoji removed #1951',
  },

  'card-charges-label': {
    question: 'What is the Grants row for Bill.com card charges called?',
    chose: 'Card charges — the row names what the page holds: charges on a family’s Bill.com card, each approved against one grant or denied.',
    because:
      'Victor asked on 2026-09-19 to make the Grants links clearer and more memorable. “Spend” named an activity, not the thing on the page, and shared the Grant Requests icon; the page’s own search and filter already said “card charges”.',
    alternatives: ['Spend', 'Card spend', 'Card purchases'],
    ref: 'Grants in one group 2026-09-19',
  },
  'award-lives-on-request': {
    question: 'Is an award its own record, or part of the grant request it came from?',
    chose: 'Part of the request. Approving a request writes the award onto it — amount, currency, the monthly schedule and every payment — and Awards lists the requests that have one.',
    because:
      'A request and its award are one to one — one decision, one award, and a new year is a new request — and every receipt, card charge, the family’s own grant list and Williams already point at the request. A separate record would move all of them for no clarity the two pages do not already give.',
    alternatives: [
      'A separate award record that points back at its request, with its own history',
      'One record per payment period, so each Signature month is its own row',
    ],
    ref: 'Grant awards wave 2026-09-19 · plan “Decision: the award lives on the grant request”',
  },
  'grant-amounts-who-edits': {
    question: 'Who can change how much TRF awards for each grant?',
    chose: 'Leads and admins — the people who can assign TRFers. Everyone else sees the amounts read-only. A change applies to awards made after it.',
    because:
      'The amounts are money policy for both countries, set rarely; the people who already decide grant requests are the people who set what a decision awards, and nobody else is asked to.',
    alternatives: [
      'Admins only',
      'Any TRFer, with every change recorded',
      'Nobody on the platform — the amounts change only in a release',
    ],
    ref: 'Grant awards wave 2026-09-19 · C-1',
  },
  'signature-digest-in-app': {
    question: 'Where does the monthly list of Signature payments due reach the people who pay them?',
    chose: 'In the app: a Payments due view on Awards, and a line on the TRFer home when any are due. No email.',
    because:
      'The standing ruling asks for a monthly digest with a one-click pause; the view carries the pause on each row, and an email would be a second copy of a list that changes as payments are recorded.',
    alternatives: [
      'A monthly email to Leads listing the payments due, linking here',
      'Both — the email on the first of the month, the view for the rest of it',
    ],
    ref: 'Grant awards wave 2026-09-19 · C-5 · DECISIONS 2026-08-30 packet C5',
  },
  'handoff-config-in-settings': {
    question: 'Where does the handoff configuration live now that it has no group of its own?',
    chose: 'The Handoff tab of Settings, read-only unless you are an admin or a lead with editing enabled.',
    because:
      'Victor said on 2026-09-14 that Handoff config can move to Settings; it was the only row left in Configure, and a one-row group is a heading, not a group.',
    alternatives: [
      'Its own Configure group with one row (the shape until 2026-09-14)',
      'Under Exploring beside Threads · Live, the surface it configures',
    ],
    ref: 'Caseload consolidation 2026-09-14 · PRD OQ8 closed',
  },
  'polls-form-sends-listed': {
    question: 'Do polls sent from a form appear on this page beside quick polls?',
    chose: 'Yes — every poll you sent is listed, and a row sent from a form says “From a form” under its title; the Origin filter cuts the list either way.',
    because:
      'Hiding rows you created would make this page’s list a lie about what you sent, and the Forms page lists sends by form rather than by poll.',
    alternatives: [
      'Quick polls only, with the form-sent ones reachable through the same Origin filter',
      'Every poll listed with nothing to tell a form send from a quick poll (the shape until 2026-09-17)',
    ],
    ref: 'Staff-lists wave 2026-09-17 · Lane C Q1',
  },
  'polls-team-edit': {
    question: 'What can a TRFer see and change on a poll another TRFer sent?',
    chose: 'Every TRFer can open every poll and read its totals. Family-level answers — named responses, the CSV rows and the “Not answered yet” names — show only the families the reader could open anyway: all of them for the sender, an admin or a lead, their own caseload for anyone else. Only the sender or an admin can edit, close, cancel or remind it.',
    because:
      'TRF works as one team, so anyone may need to see how a send is going. A family’s own answers stay with the people who look after that family, the same rule as the family page.',
    alternatives: [
      'Every TRFer also sees every family’s answers',
      'Every TRFer can also edit, close and remind any poll',
      'Each TRFer sees only the polls they sent (the shape until 2026-10-23)',
    ],
    ref: 'Wave w1023c 2026-10-23 · L1 · security review',
  },
  'remind-anonymous-count-only': {
    question: 'On an anonymous poll, does TRF see which families have not answered yet?',
    chose: 'No. TRF sees how many families have not answered and can remind them all, but no names are shown. On a named poll, the families are listed.',
    because:
      'Whoever is not on the list has answered. On an anonymous poll, a list of names would show who answered, which the poll promised not to do.',
    alternatives: [
      'List the families on anonymous polls too',
      'No reminder on anonymous polls at all',
    ],
    ref: 'Wave w1023c 2026-10-23 · L1',
  },
  'remind-once-a-day': {
    question: 'How often can TRF remind families about one poll?',
    chose: 'Once every 24 hours per poll. The page says when the last reminder went out.',
    because:
      'A reminder lands in each family’s notifications. More than one a day reads as pressure, not a nudge.',
    alternatives: [
      'Once per poll, ever',
      'No limit',
    ],
    ref: 'Wave w1023c 2026-10-23 · L1',
  },
  'checkin-custom-on-today': {
    question: 'Where does a family answer a check-in or another form TRF sends?',
    chose: 'On Today, as a to-do row with its due day and an Answer button. Quick polls stay in the Community feed.',
    because:
      'A check-in is something the program asks of the family, not a conversation. A form sent from Forms is the same kind of request.',
    alternatives: [
      'Only monthly check-ins move to Today; other forms stay on Community',
      'Everything stays on Community (the shape until 2026-10-23)',
    ],
    ref: 'Wave w1023c 2026-10-23 · L1',
  },
  // w1023c L4 — announcements reach the families they are for.
  'announcement-audience-shape': {
    question: 'How does TRF choose which families get an announcement?',
    chose: 'All families, or the same choices a check-in send offers: families picked by name, or by treatment phase and hospital within the families you can reach.',
    because: 'One way to choose families across sends and announcements, with the same rules for who may reach whom; announcements to all families stay open to every TRFer, as before.',
    alternatives: [
      'All families only, as before',
      'A separate, simpler picker just for announcements (by hospital only)',
    ],
    ref: 'w1023c L4 · reuses polls assertAudienceInScope + helpers/pollAudience.js',
  },
  'one-pin': {
    question: 'How many announcements can be pinned to the top of Community?',
    chose: 'One. Pinning a new one unpins the old one.',
    because: 'A feed with two tops has none; one pinned post is the one thing every family sees first.',
    alternatives: [
      'Up to three pinned, newest first',
      'No pin; expiry alone keeps the feed current',
    ],
    ref: 'w1023c L4 · partial unique index one_pinned_announcement',
  },
  'announcement-audience-filter': {
    question: 'What can staff filter the announcements list by?',
    chose: 'Who gets it: all families, or only some families. The Published / Taken down filter is gone, because the list only ever held live posts.',
    because: 'With audiences new, the useful question on this list is which posts went to everyone; and the toolbar’s Filters button should open something real.',
    alternatives: [
      'No filter at all: search only, with the Filters button removed from this page',
      'Pinned / expiring soon',
    ],
    ref: 'w1023c L4 item 6',
  },
  'reimbursement-row-verbs': {
    question: 'On a reimbursement or card-charge row, are Approve, Reject, Deny, Send for payment and Mark paid manually words or icons?',
    chose: 'Words. A decision that moves money reads its verb on the button; the accessible names are the same either way.',
    because:
      'The icon rule on staff rows covers navigation and edit actions — call, email, pencil, attach — and a money decision is neither; Victor ruled words on 2026-09-17.',
    alternatives: [
      'Icons with the words on hover — a check, a cross, a send arrow and a banknote, the shape the Families roster uses for its four actions',
      'Words for Approve and Reject, icons for Send for payment and Mark paid manually',
    ],
    ref: 'Staff lists wave 2026-09-17 · lane A Q2',
  },
  'grant-status-vocabulary': {
    question: 'Which words name a grant request’s stages — the family’s or the review desk’s?',
    chose: 'The family’s, on every screen: Draft · Pending · Approved · Denied · Closed · Withdrawn. Pending covers both open stages (TRF, 2026-10-24: each ask shows Pending until the team decides).',
    because:
      'Until 2026-09-17 the roster said Applied and Paid while Grant Requests, the family’s Finances tab and the family profile said Submitted and Fulfilled for the same two stages — one status, two names. A family applies for a grant; the other words were the state machine’s. Since 2026-09-19 an award is paid over time, so its last stage reads Closed rather than Paid.',
    alternatives: [
      'Submitted and Fulfilled everywhere, the roster adopting the desk’s words',
      'Paid for the last stage, as until 2026-09-19',
      'Two vocabularies — the family’s on parent screens, the desk’s on staff screens',
    ],
    ref: 'Staff-lists wave 2026-09-17 · Lane B Q2',
  },
  'conversations-row-identity': {
    question: 'Should a row on Conversations be one conversation, or one family?',
    chose: 'One row per conversation, newest activity first, with the family as the row’s name and the conversation’s title as a fact beside it.',
    because:
      'The review a TRFer files from this page is about one conversation, so a family with three conversations has three rows to open and rate, in the order they last moved.',
    alternatives: [
      'One row per family, carrying its newest conversation and a count, opening that family’s own list',
    ],
    ref: 'Staff lists wave 2026-09-17 · card D, question 1',
  },
  'conversations-consent-fold': {
    question: 'Should families without conversation access be listed on this page at all?',
    chose: 'A closed fold at the end of the list, named for what it holds, with the count on its chevron and the names inside.',
    because:
      '“Why is this family missing?” is asked on this page, and a fold is the one place a count may live; the amber notice above the list read as a warning about something a TRFer cannot change.',
    alternatives: [
      'Leave them off — the family profile is where a family’s consent state is read',
      'Keep the notice above the list, with the names in one line',
    ],
    ref: 'Staff lists wave 2026-09-17 · card D, question 2',
  },
  'williams-first-home': {
    question: 'Should the home open on Williams, or on the grant-work desk as ruled 2026-09-13?',
    chose: 'A greeting, the Williams composer with a few ideas under it, and one thing Williams noticed — the six counts became sentences with a verb and a link.',
    because:
      'Six numbers with their oldest clocks told a TRFer what existed, not what to do; a sentence from the thinking partner names the work and opens the page where it happens.',
    alternatives: [
      'The 2026-09-13 order: the desk of six counts first, Williams in a side rail',
      'Both — the composer above, the desk of counts kept below it',
    ],
    ref: 'Home + Williams wave 2026-09-17 · card A, question 1 · DECISIONS 2026-09-17',
  },
  'williams-ideas-list': {
    question: 'Which ideas does Williams offer on a blank thread, and how many?',
    chose: 'Three, drawn fresh on every visit from a pool of twelve — seven about the grant desk (applications waiting, welcome emails awaiting an acceptance, reimbursements and grant requests open, renewals, where each family’s grant stands) and five about the caseload (prep a call, who needs attention, draft notes, patterns, unclaimed tasks) — with a grant-desk idea and a caseload idea in every draw.',
    because:
      'Five fixed rows in a fixed order stopped being read, and none of them was about grants; the escalations idea named a concept that became unread messages on 2026-09-17. Victor, 2026-09-18: “reduce the number of ideas from 5 to 3 … make sure the ideas rotate.”',
    alternatives: [
      'The five fixed rows of 2026-09-17',
      'Three fixed rows, the same every visit',
      'A larger pool with no grant-desk guarantee per draw',
    ],
    ref: 'Ideas pool + Applications table wave 2026-09-18 · card, questions 1–4 · DECISIONS 2026-09-18',
  },
  'requests-decider': {
    question: 'Who decides a Travel, Family Fun or Path to Heaven request?',
    chose: 'Leads and admins — the staff who can assign TRFers. Every other TRFer cannot open Requests yet.',
    because:
      'TRF answered “the TRF team” (2026-10-24, Q8) without naming who on the team, and Leads already approved these requests before the answer, so the gate stays where it was until TRF names the people.',
    alternatives: [
      'Every TRFer decides',
      'A setting in Settings → Grants that names who decides',
      'A Lead decides above an amount, any TRFer below it',
    ],
    ref: 'Wave w1025-grants-requests · Task 5 · spec 2026-10-25 §6.1 · TRF answers 2026-10-24 Q8',
  },
  'signature-plan-email': {
    question: 'Does a renewing family get an email with its new Signature plan?',
    chose: 'Yes. A renewal’s Signature grant is created already approved, and the family is emailed its new monthly plan. A new family is not: its welcome email already names its first payment.',
    because:
      'The spec retires the approval email, but a renewing family receives no welcome email, so without this one it would never hear its new plan.',
    alternatives: [
      'No email for renewals either, as the spec wrote it',
      'An email to every family, new and renewing',
    ],
    ref: 'Wave w1025-grants-requests · Task 1 · spec 2026-10-25 §8 P2',
  },
  'record-numbers': {
    question: 'How is a family or a grant named on a call when two families share a last name?',
    chose: 'Two sequential numbers — the family’s, prefixed F, and the grant’s, prefixed G (G-0587) — minted when the record is created, never reused, never renumbered. The row leads with the grant’s number; the family’s number prints on the line only when two visible rows share the family’s last name and the child’s first name, and otherwise sits in the fold and on hover. Both are searchable as 587, G-587 or g0587.',
    because:
      'A number staff can say on a call is the one identifier that is short, stable and unambiguous; the last name plus the child’s first name is the second identifier, and it is the idiom today. City is not used — it is sparsely filled and personal on a shoulder-read list. Rows numbered before the backfill runs print a dash.',
    alternatives: [
      'The database id, shortened — unpronounceable and reads as a developer’s word',
      'TRF’s own family id scheme (trf_family_id) — reserved for TRF to assign, not minted here',
      'Last name · child · city — sparsely filled and personal',
    ],
    ref: 'Grant lists redesign 2026-09-19 · R6/R7 · DECISIONS 2026-09-19',
  },
  'assign-undo-not-confirm': {
    question: 'Should changing a family’s main contact ask “Are you sure?” first?',
    chose: 'No. Picking a TRFer changes it at once, and the notice that follows offers Undo, which puts every family back with the person it had before, unless someone has changed it since.',
    because: 'The change is fully reversible, and a confirmation on every pick slows the lead who is spreading a dozen families across the team.',
    alternatives: [
      'Ask to confirm before every change, as other staff edits do',
      'Confirm only when several families change at once',
    ],
    ref: 'Wave w1024 · plan decision 5 · amends ui-ux §3.1 · 2026-10-24',
  },
  // Wave w1024 (2026-10-24): back follows the in-app origin. NO on-screen
  // marker: the back link is on every page, so the marker would be too.
  'back-origin-aware': {
    question: 'When someone presses Back on a page, should it return to the page they came from, or always to the same list?',
    chose: 'Back returns to the page you came from, with its filters and scroll position. When there is no such page (a link from an email or a notification, or a new tab), it goes to the page’s usual list, with the filters you last used there.',
    because:
      'A family record is opened from eight places; always going to Families sent people to a list they had not been looking at, and lost their filters.',
    alternatives: [
      'Always the same list for each page (Families for a family record), whatever page you came from',
      'The browser’s own back, which can leave the platform after a link from an email',
    ],
    ref: 'Wave w1024 decision 12 · design-grammar §4.2/§4.6 · 2026-10-24',
  },
  'lifecycle-vocabulary': {
    question: 'Why does the program lifecycle say Active for a family in treatment when a Path to Heaven family is just as actively supported?',
    chose: 'The Lifecycle filter keeps its four values for now — Active · Alumni · Path to Heaven · Other — on Families, Awards and Balances and on the record’s chip, because a filter is opt-in and the record still needs the fact. The words are under review.',
    because:
      '"Active" names the treatment phase (diagnosis, active treatment, maintenance), not whether the Foundation is working with the family. Every family on a caseload is active in that sense. The vocabulary needs a word for "in treatment" that does not imply the others are not.',
    alternatives: [
      'Rename now — "In treatment · After treatment · Path to Heaven" — without the program owner ratifying the words',
      'Drop the filter from Families, keeping it only on Awards and Balances',
    ],
    ref: 'Victor 2026-09-20',
  },
  'users-group-by-role': {
    question: 'Does the Users roster open grouped by role, or flat with a Pending signups filter?',
    chose: 'Grouped by role — Admins · TRFers · Parents · Applicants — with the Group control flattening it. A pending signup sits under its role with a Pending signup chip and the approve and reject icons; the Pending signups quick filter cuts to them.',
    because:
      'Role is fixed for the account and is what you act along; status is a filter and a chip on every staff list, never the axis.',
    alternatives: [
      'Grouped by status — Pending · Active · Inactive — so the queue sits at the top',
      'Flat, newest account first (the shape until 2026-09-19)',
    ],
    ref: 'TRFer desk pages 2026-09-19 · S8 · DECISIONS 2026-09-19',
  },
  'trf-group-flag': {
    question: 'How does the platform tell a TRF WhatsApp group from an outside group?',
    chose: 'A TRF group is a Resources entry in the Community category with “TRF runs this group” ticked. TRF groups show on Community; outside groups show only on the family’s Resources page. Both keep the same country, phase and state matching.',
    because:
      'A group is a link with an audience, which is exactly what a Resources entry already is; a second list would split one job across two pages.',
    alternatives: [
      'A separate “TRF groups” list with its own page and fields',
      'A per-language axis on each group (Spanish group, English group)',
    ],
    ref: 'Wave w1023c · L3 item 1 · Victor 2026-10-23',
  },
  'resource-stale-after-180d': {
    question: 'How long can a Resources entry go unchecked before a TRFer is asked to check it again?',
    chose: '180 days. After that the row says “Check due” and the Filters dialog can show only those rows. An entry that was never checked counts as due.',
    because:
      'Phone numbers and group links drift over months, not weeks; six months keeps the due list short enough to work through.',
    alternatives: [
      '90 days',
      '365 days',
      'No clock: rely on families’ “This didn’t work” reports alone',
    ],
    ref: 'Wave w1023c · L3 item 2 · Victor 2026-10-23',
  },
  'resources-group-by-category': {
    question: 'Does the Support list open grouped by category, or flat with the category on every row?',
    chose: 'Grouped by category, in the order the family page uses; the Group control flattens it and the Category column prints only when flat. The description lives in the edit form and the family preview, not on the row.',
    because:
      'A category is fixed for the resource and is what a curator works along; printing it on every row and offering it as a filter said it twice, and the three-line row it sat in was the cost.',
    alternatives: [
      'Flat, category as a column, one line of description under the name (the shape until 2026-09-19)',
    ],
    ref: 'TRFer desk pages 2026-09-19 · S10 · DECISIONS 2026-09-19',
  },
  'family-record-anatomy': {
    question: 'What does the family record show first, and where does a TRFer change what is on it?',
    chose: 'A band with who the family is, where they are in the program and Check in; three facts under it — who holds the family, the last check-in, what has been awarded; the family’s facts and the program settings as lists you edit row by row and save; the family’s own to-do list nowhere on the page.',
    because:
      'The page said the same facts two and three times and mixed reading, editing and acting in one strip; a parent’s private to-dos are theirs and change no staff decision.',
    alternatives: [
      'The hero with seven inline selects and a strip of counts (the shape until 2026-09-19)',
      'Tabs — Overview · Program · Grants · Activity',
    ],
    ref: 'TRFer desk pages 2026-09-19 · S12 · DECISIONS 2026-09-19',
  },
  'symptom-csv-language': {
    question: 'What language is the exported symptom log in?',
    chose: 'The page’s language. The column headers, the type and severity words, Yes/No and the dates follow the language the family reads; what they typed is exported as written.',
    because: 'The family downloads it and reads it first. A parent who reads Spanish should not get a file whose every heading is English.',
    alternatives: ['Always export in English, because the care team who receives the printout may read English'],
    ref: '#2109 R3-B19',
  },

  'legal-titles-english': {
    question: 'Are the legal documents and their titles translated?',
    chose: 'No. The body and the title stay in English, and one line in the reader’s language says so above the document.',
    because: 'The English text is the one that binds. A translated title over an English body would promise a translation that is not there.',
    alternatives: ['Translate the titles only, and keep the body in English', 'Publish reviewed translations of each document'],
    ref: '#2109 R3-B9',
  },

  'machine-mark-glyph': {
    question: 'How does a reviewer see which words on a page are still machine-translated?',
    chose: 'Outside production only, a small superscript “MT” follows each string that is still a machine or draft translation. Production shows no per-string mark; the machine-translation banner covers the whole page there.',
    because: 'A mark added once, where every string is resolved, reaches every page without touching thousands of call sites, and it cannot be mistaken for this (?) marker or leak into a family’s screen.',
    alternatives: ['A marker component placed at each translated call site', 'Show the per-string mark in production as well'],
    ref: '#2109 R3-B16',
  },

  'es-first-release': {
    question: 'Do the new languages ship together or one at a time?',
    chose: 'Spanish first. The Spanish pages ship as soon as their translations are judged; Portuguese, French and Chinese follow in patch releases. Until then those readers see English wherever a translation is still empty, under the machine-translation banner.',
    because: 'Each other language takes days of translation and judging. Holding Spanish families back for all four would delay the language most TRF families asked for.',
    alternatives: ['Hold the release until all four languages are translated and judged'],
    ref: '#2109 R3-B26',
  },

  'form-edit-machine-translation': {
    question: 'When a TRFer edits a question, should families reading another language see the new wording straight away?',
    chose: 'Yes. Edited questions are machine-translated on publish without the judge loop; a TRF reviewer can still review them.',
    because: 'Victor asked that a change in the Forms section change the question in every language. Before this, an edited question showed in English to those families until someone ran the translation workflow.',
    alternatives: ['Keep translations stale (English) until the judged workflow runs'],
    ref: '#2109 · Victor 2026-09-30',
  },

  // #2239 — suggestions and comments on the committee page. Defaults from the
  // plan's appendix and the inline-editing memo §2, none ruled yet.
  // Committee PDF prose (bold runs, Notes, the staff Treatment line) — the
  // defaults built for the one-pager's prose slots.
  // The committee page's staff recommendation, its sign-off and the
  // package-cut gate — the defaults built around Victor's ruling that a
  // missing second initial only warns.
  // Decline and waitlist notices (memo 2026-10-06 rulings 2, 4, 5 and 7). Each
  // is a default built where no ruling settled the question.
  // #2234 — waitlisted families carry over to the next cycle (Victor's ruling
  // 8, 2026-10-06). The ruling settles THAT they carry; these are the defaults
  // built around it.
  // #2243 — the assistant's "Suggest edits" and fact check on the committee
  // page (inline-editing memo §4 items 2 and 3). Rulings A–C do not settle
  // these; each is a default built where no ruling did.
  // #2243 Wave D — the page's assistant controls.
  // #2244 — the assistant's acceptance-rate report. The memo names the two
  // measures; these are the definitions built where it did not pin them.
  // #2240 — the committee page editor (inline-editing memo §1, ruling C).
  // Defaults built for the editor itself; the suggestion and comment rules
  // they sit on are #2239's entries above.
  // #2242 phase B — the spelling checker inside the committee page editor.
  // E2E bug wave, wave 1 (2026-10-09): lane defaults that shipped without a
  // ruling. The rulings themselves (D-2382, D-2394, D-2398 and the rest) are
  // in DECISIONS.md and carry no marker.

  // E2E bug wave, wave 2 (2026-10-09): lane defaults that shipped without a
  // ruling, plus the fix round's open questions. Each one renders its marker
  // on the surface it governs. The rulings (D-2390 in-app cancel, D-2399 ship
  // now, D-2403 A, D-2410 local start of day, D-2420 A, D-2379 separate agents
  // PR) are in DECISIONS.md and carry no marker, so nothing here re-asks them:
  // the D-2403 reason limits and the D-2410 display live in DECISIONS.md and
  // the UI guideline. Plumbing with no screen (#2389 refresh, the #2399
  // publish script's skip of a retired question) is not marked. The one
  // integrator departure from a ruling, D-2420's touch rows, is marked below
  // as awaiting Victor.
  'canada-question-wording': {
    question: 'Do Canadian families read Canadian wording for the insurance questions themselves?',
    chose: 'Not yet. The questions keep their current wording, and only the answers change for Canada.',
    because: 'The form can now word a question by country (military service, money, distance and travel already do), but nobody has written the Canadian insurance wording yet.',
    alternatives: [
      'Ask about “supplemental or extended health insurance” for Canada, as the workbook does',
      'Use one wording that fits both countries',
    ],
    ref: '#2399 · E2E wave 2 W2-CANADA · cross-section labels since #2523',
  },

  'canada-band-thresholds': {
    question: 'Where do the Canadian income, travel-spend and distance bands draw their lines?',
    chose:
      'The income and travel-spend bands keep the US numbers, written in Canadian dollars (for example “Less than CA$25,000”). '
      + 'The distance bands are the mile bands turned into kilometres and rounded (for example “Less than 80 km”).',
    because: 'TRF has not set Canadian bands, and keeping the same lines lets staff compare families across the two countries.',
    alternatives: [
      'Convert the dollar bands at an exchange rate',
      'Use round kilometre bands, such as “Less than 100 km”',
      'Let TRF set separate Canadian bands',
    ],
    ref: '#2523 · renewal fix wave L4-CANADA · values cad_*, km_*',
  },

  'receipt-capture-follow-up': {
    question: 'After a family adds a receipt, how long does the page keep checking what happened to it?',
    chose:
      'It checks every few seconds for up to three minutes, then says “Added — it’s in your receipts”. '
      + '“See it” goes to the receipts that still need the family, to the receipts with TRF, or opens the receipt itself, depending on where it landed.',
    because: 'Most receipts are read in under a minute, and a family should not be left looking at “reading it now”.',
    alternatives: [
      'Keep checking until the receipt is read, however long that takes',
      'Say “Added” at once and let the family look for it',
    ],
    ref: '#2378 · E2E wave 2 W2-RECEIPTS',
  },

  'receipt-refused-type-wording': {
    question: 'What does a family see when they pick a file the receipts page cannot take?',
    chose:
      '“Only JPEG, PNG or PDF files can be added.”, in their language, the same on the page and in chat. '
      + 'The file picker shows only those types, but the camera still opens on a phone.',
    because: 'One plain sentence names what works, and narrowing the camera too would stop phones from opening it.',
    alternatives: [
      'Name the type of the file they picked',
      'Narrow the camera to the same types',
    ],
    ref: '#2380 · E2E wave 2 W2-RECEIPTS · unsupported_file_type',
  },

  'receipt-note-english-only': {
    question: 'When a receipt is read automatically, does it fill in the note for families who use another language?',
    chose: 'No. The note is filled in only for families using English. Others write their own note or leave it empty.',
    because: 'The automatic summary is written in English, and a family should not find English text in their own note.',
    alternatives: [
      'Translate the summary into the family’s language',
      'Fill in the English summary for everyone, as before',
    ],
    ref: '#2409 · E2E wave 2 W2-RECEIPTS · stampLanguageFor header, then preference, then English',
  },

  'chat-receipt-thread-note': {
    question: 'When a family sends a receipt from chat, does the conversation keep a record of it?',
    chose:
      'A short note in the conversation says the receipt was sent and points to the Grants page, with an “Open Grants” button in the dialog. '
      + 'The note is only on this screen: it is gone after a refresh and does not change language later.',
    because: 'The family needs to know where to follow the receipt, and saving the note would mean changing how conversations are stored.',
    alternatives: [
      'Save the note with the conversation, so it is still there later',
      'Show only a pop-up, with no note in the conversation',
    ],
    ref: '#2379 · E2E wave 2 W2-RECEIPTS fix round · local only, not persisted',
  },

  'reaction-confirmation': {
    question: 'How does someone know their reaction to an announcement went through?',
    chose:
      'A screen reader says “Heart added” or “Heart removed”, and the count changes. Nothing new appears on screen. '
      + 'While a reaction is being saved, its button cannot be pressed again but stays where it is.',
    because: 'The count already shows sighted readers that it worked, and a pop-up for every heart would be noise.',
    alternatives: [
      'A short visible confirmation, as the support walkthrough describes',
      'A pop-up message',
    ],
    ref: '#2426 · E2E wave 2 W2-FAMILY-POLISH · runbook W7.2',
  },

  'data-request-pending-shape': {
    question: 'In Settings, what does a data export or deletion request look like while TRF works on it?',
    chose:
      'The row says “Requested on <date>. A TRF team member will follow up.”, and the delete button becomes a greyed “Requested” button. '
      + 'If the request list cannot be read, the rest of Settings still loads and the rows look as if nothing was asked.',
    because: 'A parent should see that the request is in, and one failed read should not blank the whole page.',
    alternatives: [
      'Let the parent ask again, and say it is already open',
      'Show an error on the rows when the request list cannot be read',
    ],
    ref: '#2391 · E2E wave 2 W2-SETTINGS · consent read omits data_requests on failure',
  },

  'account-closure-row-roles': {
    question: 'Who sees the “Close your account” row in Settings?',
    chose: 'Everyone who can open Settings, staff included.',
    because: 'Any account can be closed, and hiding the row by role would need its own rule.',
    alternatives: [
      'Parents only',
    ],
    ref: '#2390 · E2E wave 2 W2-SETTINGS · D-2390 in-app cancel is ruled; the row scope is the default',
  },

  'account-closure-page-details': {
    question: 'How does the close-account page behave in the edge cases?',
    chose:
      'If the closure date cannot be read, the page says so and still offers to schedule, which never sets a second date. '
      + 'If the date has passed but the account is not closed yet, asking again keeps that date. The page is filed under Account and has no back link.',
    because: 'Scheduling twice is harmless, so a failed read should not block a parent who wants to close.',
    alternatives: [
      'Block scheduling until the date can be read',
      'Set a new date when the old one has passed',
    ],
    ref: '#2390 · E2E wave 2 W2-SETTINGS',
  },

  'treatment-cycle-field-order': {
    question: 'In what order are the treatment cycle fields?',
    chose: 'Cycle number, cycle length, then start date. On a phone the first two share a row and the date gets its own.',
    because: 'A date needs the most room, and putting it last lets it have a full row on a phone.',
    alternatives: [
      'Keep the start date in the middle, as before',
    ],
    ref: '#2436 · E2E wave 2 W2-SETTINGS',
  },

  'letter-templates-home': {
    question: 'Where do referral letter templates live?',
    chose: 'On the Referral codes page, in a Letters tab beside the codes that use them.',
    because: 'The people who make a code are the ones who pick and edit its letter. Forms holds the questions families answer, with publishing and translation steps a letter does not need.',
    alternatives: [
      'A Letters section in Forms',
      'Settings → Applications, beside each cycle’s letter values',
    ],
    ref: '2026-11-01 · referral letter templates',
  },

  'letterhead-defaults': {
    question: 'What closes a TRF referral email?',
    chose: 'One signature at the bottom: the dove, the signer’s name, title, phone, trf.org and email, TRF’s mailing address and the confidentiality notice. No header and no “Kind regards” picture; a Signature checkbox on each letter leaves it out.',
    because: 'It follows Melanie’s own email: the hospital letter, a line, the family letter, signed once (Victor, 2026-11-02).',
    alternatives: [
      'A signature under each letter',
      'Signature off unless ticked',
    ],
    ref: '2026-11-02 · one email, signed once',
  },

  'code-limit-counts-every-application': {
    question: 'What uses up a place on a referral code?',
    chose: 'Every application started with the code, drafts included: the hospital has 5 tickets. A withdrawn application gives its place back. A family who goes quiet keeps theirs; a TRFer reaches out to the family or the hospital, or raises the limit.',
    because: 'Victor, 2026-11-02: "it is like the hospital individual has 5 tickets", reversing count-submitted-only.',
    alternatives: [
      'Count submitted applications only',
      'Keep a withdrawn application’s place used',
    ],
    ref: '2026-11-02 · referral code limit (Melanie)',
  },

  'family-code-parent-contact': {
    question: 'Is a Family code for one parent, or can it still be shared?',
    chose: 'It can still be shared. The parent you add is who the letter greets and goes to; any family holding the code can still apply with it.',
    because: 'Every code opens many applications (2026-10-28), and a TRFer still needs an email to send the letter.',
    alternatives: [
      'One parent per Family code, used once',
    ],
    ref: '2026-11-01 · referral letter templates',
  },

  'select-list-edge-gap': {
    question: 'How close to the screen edge can an open dropdown list sit?',
    chose: 'Every dropdown list now keeps a small gap from the screen edge, is never wider than the screen, and lets long choices wrap.',
    because: 'The time-zone list ran off a phone screen, and the same fix suits every dropdown.',
    alternatives: [
      'Fix only the time-zone list',
    ],
    ref: '#2421 · E2E wave 2 W2-SETTINGS · Select default collisionPadding 16',
  },


  'task-clear-time-makes-all-day': {
    question: 'What happens when someone removes the time from a timed task?',
    chose: 'It becomes an all-day task. Adding a time back makes it a timed task again.',
    because: 'A task with a date and no time is an all-day task, whichever way it got there.',
    alternatives: [
      'Keep it timed at its old time until the time is changed',
    ],
    ref: '#2410 · E2E wave 2 W2-TASKS-ALLDAY · PATCH always carries all_day',
  },

  'touch-target-series-rows': {
    question: 'On a phone, how do the small text buttons in a repeating task’s date list reach full finger size?',
    chose:
      'Each button grows its own box to full finger height, and each date row in that list is that tall on a phone, so the buttons do not overlap. '
      + 'The buttons are only a little wider than their word, so short words such as “Done” stay under full finger width.',
    because:
      'The ruled invisible tap area (D-2420 A) never received a tap inside that list, because the rows around it took the taps. '
      + 'Text buttons sit close together, and wider tap areas would overlap their neighbours.',
    alternatives: [
      'Keep the rows the same height, as D-2420 A ruled, and accept smaller tap areas there',
      'Full finger width too, with more space between the buttons',
      'Move Done and Cancel into a menu for each date',
    ],
    ref: '#2420 · E2E wave 2 fix round · integrator default departing from D-2420 A (“visuals unchanged”), awaiting Victor',
  },

  // ── 2026-10-10 application review redesign, lane L7 (single committee decision) ──
  // ── 2026-10-11 fix wave d, lane L4 (receipt not approved) ──
  'receipt-not-approved-next-step': {
    question: 'When a receipt isn’t approved, should the family add a new one, or fix and resend the same one?',
    chose: 'Add a new one. A receipt that wasn’t approved stays as it is, with TRF’s reason, and its details offer “Add a new receipt”, which opens the add-expense form.',
    because: 'The old footer said the expense was with TRF and could not be edited, so a family told to upload a dated receipt had nowhere to go. A receipt that wasn’t approved is decided, so it can no longer be edited.',
    alternatives: [
      'Reopen the same receipt so the family can fix it and send it again',
      'Only say what to do, with no button',
    ],
    ref: 'Receipts 2026-10-11',
  },

  // ── 2026-10-13 wave w1013c, lane L5 (#2199 Canada switch) ──
  // #2199 — the notice a Canadian family sees while features.canada_intake_closed is on.
  // ── 2026-10-14 wave w1015, lane L3 (#2956 payments wording) ──
  'grants-how-payments-work': {
    question: 'Is this how TRF wants to explain payments to families?',
    chose:
      'A short “How payments work” list at the foot of the Grants page: the Signature grant is paid monthly; '
      + 'bank changes are made in Bill.com; new statements and payment changes reach TRF within 30 days; '
      + 'Travel and Family Fun payments go out on the 1st; TRF is not responsible for late or other fees; '
      + 'anything urgent goes to the case manager by email.',
    because: 'These are the rules TRF already tells families, and the page that shows the money is where a family looks for them.',
    alternatives: [
      'TRF rewrites the wording before families see it',
      'Show the rules once, in the welcome email, instead of on the page',
    ],
    ref: '#2956 · TRF to approve the payments wording · Azalea gap report AZ-13, AZ-14, AZ-16, AZ-35',
  },

  // ── 2026-10-18 wave w1018, lane L4 (#2956 case manager contact) ──
  'case-manager-contact-address': {
    question: 'Which address should a family see for their case manager?',
    chose: 'The TRFer’s sign-in address.',
    because: 'It is the one address the platform already holds for every TRFer, so a family can write to their case manager today.',
    alternatives: [
      'A work address staff enter on their profile',
      'One shared TRF inbox',
    ],
    ref: '#2956 · TRF to choose the address families see',
  },
  // ── 2026-10-18 wave w1018, lane L3 (#2952 offer email interim paragraphs) ──
  'offer-email-interim-packet': {
    question: 'Is this what the welcome email should say until the DocuSign envelope is built?',
    chose: 'The packet, onboarding and contacts paragraphs.',
    because:
      'A family welcomed today learns from the email that the award letter and acceptance form come separately by DocuSign, '
      + 'that the letter dates the onboarding meeting, and that Azalea and Melanie Cooper are their TRF contacts.',
    alternatives: [
      'Wait for the DocuSign envelope',
      'Send the welcome only after DocuSign',
      'No contact names in the email',
    ],
    ref: '#2952 · w1015 plan ruling R3 · DocuSign envelope #2918, #1069',
  },
  // ── 2026-10-18 wave w1018, lane L2 (#2954 slice A) ──
  // Marked on GrantUsageBar (Signature grants, beside the applied figure) and on the Awards "Used" header.
  'signature-receipts-prove-not-reimburse': {
    question: 'What does a receipt linked to a Signature award mean?',
    chose: 'Proof of how the payment was used: never paid back and never counted against the award.',
    because:
      'TRF pays the Signature award every month and asks for a receipt for every payment. Counting that receipt against the award added the same money twice, so a correctly paid award showed less left, or even over budget, and the receipt could be paid back a second time.',
    alternatives: [
      'Keep counting receipts against the award (before 2026-10-18)',
      'A separate “proof” receipt kind',
    ],
    ref: '#2954 slice A · wave w1018 · Azalea gap report AZ-14, AZ-15',
  },
  // ── 2026-10-22 wave w1022, lane D2 (#3054) ──
  // Server-side; no on-screen marker yet (the impersonation dialog in UserRoster.tsx is the natural place).
  'impersonation-menu-covers-writes': {
    question: 'When staff view the app as someone and leave a menu unticked, is that menu closed to looking as well as changing?',
    chose: 'Closed to both: an unticked menu cannot be read or changed in that session, and a ticked menu can be read and changed.',
    because: 'Menus are not read-only today, so the tick is the whole surface; a read-only tick would be a second setting nobody has asked for yet.',
    alternatives: [
      'A ticked menu is read-only; changes need a second tick',
      'Unticked menus stay readable and only changes are blocked',
    ],
    ref: '#3054 · wave w1022 lane D2 · audit L6',
  },
  // ── 2026-10-23: a family's conversation permission is for TRF admins only ──
  'superuser-means-admin': {
    question: 'When a family lets TRF’s program leads read their conversations, who exactly is that?',
    chose: 'Everyone with the admin role. They read on Admin → Conversations, and every conversation they open is recorded.',
    because: 'Victor named superusers such as Ana and Azalea, and the admin role is the one TRF already gives its program leads; a new role would be a second list to keep in step.',
    alternatives: [
      'A named list of people, set in Settings, narrower than every admin',
      'Admins plus Lead TRFers',
    ],
    ref: 'Victor 2026-10-23 · admin conversation reader',
  },
  'admin-reviews-consent-gate': {
    question: 'Should the conversation review queue show only families who allow their conversations to be read?',
    chose: 'Yes. A family who has not turned on “Let TRF’s program leads read your conversations” is missing from the queue, and opening one of its conversations by link answers “not found”.',
    because: 'The family’s permission is the one promise made about who reads their conversations; a quality queue reading every family would break it.',
    alternatives: [
      'Keep reviewing every family, since the queue rates the assistant rather than the family',
      'Show every family but hide the message text unless the family allows it',
    ],
    ref: 'Victor 2026-10-23 · admin conversation reader',
  },
  'import-add-incomplete-family': {
    question: 'Can an incomplete family be added from Import families?',
    chose: 'Yes. Add works while answers are still missing, and the family’s import page keeps listing what is missing after Add.',
    because: 'Paper forms often arrive with gaps a TRFer fills later on the family record; holding Add back would leave a family off TRF Imagine for one blank answer.',
    alternatives: [
      'Add stays off until every required answer is filled',
    ],
    ref: '#3235 · (?) A',
  },
  'import-remove-added-family': {
    question: 'How does a TRFer undo adding the wrong family?',
    chose: 'An added family’s import page offers “Remove this family” until the parent signs in. It deletes the family, its parent account, its grants and its documents, and TRF Imagine can still refuse if a payment or staff work is recorded.',
    because: 'TRF Imagine has no other way to delete a family, and once a parent has signed in the family is real to them.',
    alternatives: [
      'No way back: a wrong family is closed by hand on its family record',
      'Remove stays possible after sign-in, with a stronger warning',
    ],
    ref: '#3235 · (?) B',
  },
  'import-media-release-disagree': {
    question: 'What does a media release answer of “I Disagree” do?',
    chose: 'It is shown under Not brought over and does not stop Add. Add saves the answer with the family, under “From the Google Form” on its record, as the form’s words. TRF Imagine has no media release field that acts on it.',
    because: 'TRF Imagine has no media release field yet, and saying no to publicity is not a reason to keep a family out of the program.',
    alternatives: [
      'Add a media release field on the family and bring the answer over',
      'Hold Add until a TRFer has read the answer',
    ],
    ref: '#3235 · (?) C',
  },
} as const satisfies Record<string, PendingDecisionSpec>;

export type PendingDecisionId = keyof typeof PENDING_DECISIONS;
